import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createNotificationsApi, type NotificationsApi } from "./api.js";
import type {
  MerchantNotification,
  MerchantAttentionNotification,
  MerchantAttentionNotificationPage,
  NotificationListFilters,
  NotificationListPage,
  NotificationPreferenceRecord,
} from "./models.js";

export interface NotificationsTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface NotificationsService {
  loadNotifications(filters: NotificationListFilters): Promise<NotificationListPage>;
  loadAttentionNotifications(
    limit: number,
    cursor?: string,
  ): Promise<MerchantAttentionNotificationPage>;
  markAttentionRead(notificationId: string): Promise<MerchantAttentionNotification>;
  loadPreferences(): Promise<NotificationPreferenceRecord>;
  markRead(notificationId: string): Promise<MerchantNotification>;
  retryDelivery(notificationId: string): Promise<MerchantNotification>;
  savePreferences(record: NotificationPreferenceRecord): Promise<NotificationPreferenceRecord>;
}

export interface NotificationsServiceOptions {
  api?: NotificationsApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
  telemetry?: NotificationsTelemetry;
}

export function createNotificationsService(
  options: NotificationsServiceOptions,
): NotificationsService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? (() => crypto.randomUUID());

  return {
    loadNotifications(filters) {
      return track(
        telemetry,
        "merchant_notifications_loaded",
        () => api.listNotifications(filters),
        { status: filters.status },
      );
    },
    loadAttentionNotifications(limit, cursor) {
      return track(
        telemetry,
        "merchant_attention_notifications_loaded",
        () => api.listAttentionNotifications(limit, cursor),
        { limit, cursor },
      );
    },
    markAttentionRead(notificationId) {
      return track(
        telemetry,
        "merchant_attention_notification_marked_read",
        () => api.markAttentionRead(notificationId),
        { notificationId },
      );
    },
    loadPreferences() {
      return track(
        telemetry,
        "merchant_notification_preferences_loaded",
        () => api.getPreferences(),
        {},
      );
    },
    markRead(notificationId) {
      return track(
        telemetry,
        "merchant_notification_marked_read",
        () => api.markRead(notificationId, idempotencyKeyFactory()),
        { notificationId },
      );
    },
    retryDelivery(notificationId) {
      return track(
        telemetry,
        "merchant_notification_delivery_retry_requested",
        () => api.retryDelivery(notificationId, idempotencyKeyFactory()),
        { notificationId },
      );
    },
    savePreferences(record) {
      return track(
        telemetry,
        "merchant_notification_preferences_saved",
        () => api.updatePreferences(record, idempotencyKeyFactory()),
        {},
      );
    },
  };
}

async function track<T>(
  telemetry: NotificationsTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): NotificationsApi {
  if (client === undefined) {
    throw new Error("Notifications service requires an API client.");
  }

  return createNotificationsApi(client);
}

function browserTelemetry(): NotificationsTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
